import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings, Fingerprint, AlertTriangle, RefreshCw, CheckCircle2, Lock, Copy, CheckCheck,
  Download, FileCode2, FileText, Package, Database, ChevronDown, ChevronRight, Shield,
  Zap, Globe, Send, Terminal, Info
} from 'lucide-react';
import { useSecurity } from '../security/SecurityContext';
import { API, BACKEND, authFetch } from '../lib/api';
import { SqlCredentialsManager } from '../components/SqlCredentialsManager';
import { BulkResyncPanel } from '../components/BulkResyncPanel';
import ProductionERPIntegrationPanel from './ProductionERPIntegrationPanel';

// ── WebhookTestPanel ──────────────────────────────────────────────────────────
// Explains ZIP vs Webhook architecture and lets admin test a provisioning.
function WebhookTestPanel() {
  const [form, setForm] = useState({
    tierCode: 'Lite', industryCode: 'APPAREL', companyName: '', tradeName: '',
    adminName: '', adminEmail: '', adminPhone: '', country: 'IND',
    expectedUsers: '5', defaultPassword: ''
  });
  const [state, setState]   = useState('idle'); // idle | loading | success | error
  const [result, setResult] = useState(null);
  const [tiers, setTiers]   = useState([]);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [showHmac, setShowHmac]     = useState(false);

  useEffect(() => {
    // Load tier list
    fetch(`${API}/signup/pricing-tiers`)
      .then(r => r.json())
      .then(d => setTiers(d.tiers || []))
      .catch(() => {});
    // Load webhook info
    authFetch(`${API}/licensor/webhook-info`)
      .then(r => r.json())
      .then(d => setWebhookInfo(d))
      .catch(() => {});
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const runTest = async () => {
    if (!form.companyName || !form.adminEmail) {
      alert('Company name and Admin email are required.');
      return;
    }
    setState('loading');
    setResult(null);
    try {
      const r = await authFetch(`${API}/licensor/test-provision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const d = await r.json();
      if (!r.ok) {
        setState('error');
        setResult(d);
      } else {
        setState('success');
        setResult(d);
      }
    } catch (e) {
      setState('error');
      setResult({ error: e.message });
    }
  };

  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, boxSizing: 'border-box', fontFamily: 'inherit' };
  const lbl = { display: 'block', fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', marginBottom: 4, letterSpacing: 0.3 };

  return (
    <div style={{ border: '2px solid #8b5cf6', borderRadius: 14, overflow: 'hidden', background: '#fff' }}
      data-testid="ks-webhook-test-panel">
      {/* Header */}
      <div style={{ background: '#f5f3ff', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Zap size={16} color="#7c3aed" strokeWidth={2.5} />
        <span style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>Webhook / Direct Provisioning</span>
        <span style={{ background: '#fff', border: '1px solid #8b5cf6', color: '#7c3aed', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5 }}>BACKEND-TO-BACKEND</span>
      </div>

      <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Architecture explanation */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ background: '#ecfdf5', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <Globe size={13} color="#16a34a" />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d' }}>Webpage ZIP (Public Flow)</span>
            </div>
            <div style={{ fontSize: 11, color: '#166534', lineHeight: 1.6 }}>
              Customer visits your website → picks tier → OTP email verification → (mock) payment → account created.<br/>
              <strong>No OTP bypass.</strong> Drop the ZIP on Netlify/Vercel/S3.
            </div>
          </div>
          <div style={{ background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <Terminal size={13} color="#7c3aed" />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6d28d9' }}>Webhook (Backend-to-Backend)</span>
            </div>
            <div style={{ fontSize: 11, color: '#4c1d95', lineHeight: 1.6 }}>
              Your trusted server POSTs a HMAC-signed request → full provisioning: DB creation, ERP seeding, email sent — <strong>no OTP needed.</strong>
            </div>
          </div>
        </div>

        {/* Webhook endpoint info */}
        {webhookInfo && (
          <div style={{ background: '#0f172a', borderRadius: 8, padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>WEBHOOK ENDPOINT</span>
              <button
                onClick={() => setShowHmac(!showHmac)}
                style={{ background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer' }}
              >
                {showHmac ? 'Hide' : 'Show'} HMAC example
              </button>
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#60a5fa', marginBottom: 4 }}>
              POST {webhookInfo.webhookEndpoint}
            </div>
            <div style={{ fontFamily: 'monospace', fontSize: 10, color: '#64748b' }}>
              X-Webhook-Signature: sha256=&lt;hmac&gt; &nbsp;|&nbsp; X-Webhook-Timestamp: &lt;unix-seconds&gt;
            </div>
            {showHmac && (
              <pre style={{ marginTop: 10, color: '#86efac', fontSize: 10, lineHeight: 1.6, whiteSpace: 'pre-wrap', borderTop: '1px solid #1e293b', paddingTop: 10 }}>
{webhookInfo.hmacExample}
              </pre>
            )}
          </div>
        )}

        {/* Test form */}
        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <Send size={13} color="#7c3aed" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>Test: Admin Console Direct Provision</span>
            <span style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>(bypasses OTP — admin trust)</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={lbl}>Tier *</label>
              <select value={form.tierCode} onChange={e => f('tierCode', e.target.value)} style={inp} data-testid="tp-tier-select">
                {tiers.length > 0
                  ? tiers.map(t => <option key={t.code} value={t.code}>{t.name} {t.billingCycle === 'custom' ? '(Contact Sales)' : t.price > 0 ? `(₹${t.price})` : '(Free)'}</option>)
                  : ['Lite','Express','Standard','Professional','Enterprise'].map(c => <option key={c} value={c}>{c}</option>)
                }
              </select>
            </div>
            <div>
              <label style={lbl}>Industry Code *</label>
              <input value={form.industryCode} onChange={e => f('industryCode', e.target.value)} style={inp} placeholder="e.g. APPAREL" data-testid="tp-industry-input" />
            </div>
            <div>
              <label style={lbl}>Country</label>
              <input value={form.country} onChange={e => f('country', e.target.value)} style={inp} placeholder="IND" data-testid="tp-country-input" />
            </div>
            <div>
              <label style={lbl}>Company Name *</label>
              <input value={form.companyName} onChange={e => f('companyName', e.target.value)} style={inp} placeholder="e.g. Test Corp Pvt Ltd" data-testid="tp-company-input" />
            </div>
            <div>
              <label style={lbl}>Trade / Short Name</label>
              <input value={form.tradeName} onChange={e => f('tradeName', e.target.value)} style={inp} placeholder="Optional" data-testid="tp-tradename-input" />
            </div>
            <div>
              <label style={lbl}>Admin Name</label>
              <input value={form.adminName} onChange={e => f('adminName', e.target.value)} style={inp} placeholder="e.g. John Doe" data-testid="tp-adminname-input" />
            </div>
            <div>
              <label style={lbl}>Admin Email *</label>
              <input type="email" value={form.adminEmail} onChange={e => f('adminEmail', e.target.value)} style={inp} placeholder="admin@yourcompany.com" data-testid="tp-email-input" />
            </div>
            <div>
              <label style={lbl}>Admin Phone</label>
              <input value={form.adminPhone} onChange={e => f('adminPhone', e.target.value)} style={inp} placeholder="9999999999" data-testid="tp-phone-input" />
            </div>
            <div>
              <label style={lbl}>Expected Users</label>
              <input type="number" value={form.expectedUsers} onChange={e => f('expectedUsers', e.target.value)} style={inp} min="1" data-testid="tp-users-input" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Default Password (optional — random if blank)</label>
              <input value={form.defaultPassword} onChange={e => f('defaultPassword', e.target.value)} style={inp} placeholder="Leave blank to auto-generate" data-testid="tp-password-input" />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={runTest}
              disabled={state === 'loading'}
              data-testid="tp-run-btn"
              style={{ padding: '9px 22px', background: state === 'loading' ? '#c4b5fd' : '#7c3aed', color: '#fff', border: 0, borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: state === 'loading' ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Send size={13} />{state === 'loading' ? 'Provisioning…' : 'Run Test Provision'}
            </button>
            {state !== 'idle' && (
              <button onClick={() => { setState('idle'); setResult(null); }}
                style={{ padding: '8px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 11, cursor: 'pointer', color: '#475569' }}>
                Clear
              </button>
            )}
          </div>

          {/* Result */}
          {result && (
            <div style={{ marginTop: 12, padding: '12px 16px', background: state === 'success' ? '#0f172a' : '#fef2f2', border: `1px solid ${state === 'success' ? '#10b981' : '#fca5a5'}`, borderRadius: 8 }}>
              {state === 'success' ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <CheckCircle2 size={14} color="#10b981" />
                    <span style={{ fontWeight: 700, color: '#10b981', fontSize: 12 }}>Provisioning successful!</span>
                    <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>{result.clientId}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {result.logins && Object.entries(result.logins).map(([role, cred]) => (
                      <div key={role} style={{ background: '#1e293b', borderRadius: 6, padding: '8px 10px' }}>
                        <div style={{ fontSize: 10, color: '#64748b', textTransform: 'uppercase', marginBottom: 3 }}>{role}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#60a5fa' }}>{cred.username}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#34d399' }}>{cred.password}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 10, color: '#f59e0b' }}>{result.note}</div>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                  <AlertTriangle size={13} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#991b1b' }}>{result.error || 'Provisioning failed'}</div>
                    {result.message && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 3 }}>{result.message}</div>}
                    {result.details && (
                      <pre style={{ fontSize: 10, color: '#b91c1c', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(result.details, null, 2)}
                      </pre>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Handles the "Generate → Preview → Download" flow for a single ZIP package.
function ZipDownloadPanel({ title, filename, description, downloadUrl, color, accentBg, testId, badges, previewUrl }) {
  const [state, setState]         = useState('idle'); // idle | generating | ready | downloading
  const [manifest, setManifest]   = useState(null);
  const [expanded, setExpanded]   = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);

  const generate = useCallback(async () => {
    setState('generating');
    try {
      const r = await authFetch(`${API}/security/zip-manifest`);
      const d = await r.json();
      setManifest(d);
      setGeneratedAt(new Date().toISOString());
      setState('ready');
    } catch (e) {
      alert('Failed to load manifest: ' + e.message);
      setState('idle');
    }
  }, []);

  const download = async () => {
    setState('downloading');
    try {
      // Use direct URL navigation with token as query param.
      // This works for both the button click AND when users share/bookmark the URL.
      const token = sessionStorage.getItem('erp_security_token') || localStorage.getItem('erp_security_token');
      if (!token) { alert('Not authenticated. Please log in again.'); setState('ready'); return; }
      const url = `${API}${downloadUrl}?token=${encodeURIComponent(token)}`;
      // Create a temporary link and trigger download (preserves filename)
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => setState('ready'), 1500);
    } catch (err) {
      alert('Download failed: ' + err.message);
      setState('ready');
    }
  };

  const fileList = manifest
    ? (filename.includes('integration') ? manifest.integration?.files : manifest.webpackage?.files) || []
    : [];

  const formatBytes = (b) => {
    if (!b) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b/1024).toFixed(1)} KB`;
    return `${(b/1048576).toFixed(2)} MB`;
  };
  const timeAgo = (iso) => {
    if (!iso) return '';
    const secs = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (secs < 10) return 'just now';
    if (secs < 60) return `${secs}s ago`;
    return `${Math.floor(secs/60)}m ago`;
  };

  return (
    <div style={{ border: `2px solid ${color}`, borderRadius: 14, overflow: 'hidden', background: '#fff' }}
      data-testid={testId}>
      {/* Header */}
      <div style={{ background: accentBg, padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Package size={16} color={color} strokeWidth={2.5} />
          <span style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{title}</span>
          <span style={{ background: '#fff', border: `1px solid ${color}`, color, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5, letterSpacing: 0.3 }}>{filename}</span>
        </div>
        {generatedAt && (
          <span style={{ fontSize: 10, color: '#64748b', fontStyle: 'italic' }}>
            Manifest loaded {timeAgo(generatedAt)}
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '16px 20px' }}>
        <p style={{ fontSize: 12, color: '#475569', lineHeight: 1.7, marginBottom: 12 }}>{description}</p>

        {/* File badge list */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
          {badges.map(b => (
            <span key={b} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 5, fontFamily: 'monospace' }}>{b}</span>
          ))}
        </div>

        {/* DNA safety note */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 7, padding: '6px 12px', marginBottom: 14 }}>
          <Shield size={11} color="#16a34a" strokeWidth={2.5} />
          <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}>Download does NOT modify DNA Keys or Owner Settings</span>
        </div>

        {/* Live Preview button — always available, no generate step needed */}
        {previewUrl && (
          <div style={{ marginBottom: 12 }}>
            <a
              href={`${BACKEND}/api${previewUrl}?token=${encodeURIComponent(sessionStorage.getItem('erp_security_token') || localStorage.getItem('erp_security_token') || '')}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testId}-preview-btn`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0', borderRadius: 7, fontWeight: 700, fontSize: 12, textDecoration: 'none', cursor: 'pointer' }}
            >
              <span style={{ fontSize: 13 }}>&#128065;</span> Live Preview Signup Page
            </a>
            <span style={{ fontSize: 11, color: '#64748b', marginLeft: 10 }}>Opens in new tab — tests the full flow against your live backend</span>
          </div>
        )}

        {/* State: idle */}
        {state === 'idle' && (
          <button
            className="btn btn-primary"
            style={{ padding: '10px 22px', background: color, border: `1px solid ${color}`, color: '#fff', fontWeight: 700, fontSize: 12 }}
            onClick={generate}
            data-testid={`${testId}-generate-btn`}
          >
            <RefreshCw size={13} style={{ marginRight: 6 }} />
            Check Latest Files &amp; Prepare Download
          </button>
        )}

        {/* State: generating */}
        {state === 'generating' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
            <RefreshCw size={16} className="spin" color={color} />
            <span style={{ fontSize: 13, color, fontWeight: 700 }}>Scanning latest codebase files…</span>
          </div>
        )}

        {/* State: ready or downloading — show manifest + download button */}
        {(state === 'ready' || state === 'downloading') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Manifest summary */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setExpanded(p => !p)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 14px', background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircle2 size={13} color="#16a34a" strokeWidth={2.5} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                    {fileList.length} files ready — freshly scanned from current codebase
                  </span>
                </div>
                {expanded ? <ChevronDown size={13} color="#64748b" /> : <ChevronRight size={13} color="#64748b" />}
              </button>
              {expanded && (
                <div style={{ borderTop: '1px solid #e2e8f0', maxHeight: 240, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        <th style={{ textAlign: 'left', padding: '5px 14px', color: '#475569', fontWeight: 700 }}>File</th>
                        <th style={{ textAlign: 'right', padding: '5px 14px', color: '#475569', fontWeight: 700 }}>Size</th>
                        <th style={{ textAlign: 'left', padding: '5px 14px', color: '#475569', fontWeight: 700 }}>Last Modified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fileList.map((f, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '5px 14px', fontFamily: 'monospace', color: '#0f172a' }}>
                            <FileCode2 size={10} color={color} style={{ marginRight: 5 }} />
                            {f.path}
                            {f.note && <span style={{ color: '#64748b', fontStyle: 'italic', marginLeft: 6 }}>({f.note})</span>}
                          </td>
                          <td style={{ padding: '5px 14px', textAlign: 'right', color: '#64748b' }}>{formatBytes(f.size)}</td>
                          <td style={{ padding: '5px 14px', color: f.generated ? '#16a34a' : '#64748b', fontWeight: f.generated ? 700 : 400 }}>
                            {f.generated ? 'Generated now' : (f.lastModified ? new Date(f.lastModified).toLocaleString() : '—')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Download + Re-generate buttons */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                className="btn"
                style={{ padding: '10px 24px', background: color, border: `1px solid ${color}`, color: '#fff', fontWeight: 700, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={download}
                disabled={state === 'downloading'}
                data-testid={`${testId}-download-btn`}
              >
                {state === 'downloading'
                  ? <><RefreshCw size={13} className="spin" /> Building &amp; Downloading…</>
                  : <><Download size={13} /> Download Fresh ZIP</>}
              </button>
              <button
                className="btn btn-ghost"
                style={{ fontSize: 11, padding: '8px 14px', color: '#64748b' }}
                onClick={() => { setState('idle'); setManifest(null); setExpanded(false); }}
                disabled={state === 'downloading'}
              >
                Reset
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Environment Config Card ──────────────────────────────────────────────────
function EnvironmentConfigCard() {
  const [open, setOpen]           = useState(true);
  const [copiedKey, setCopiedKey] = useState(null);

  const backendUrl   = BACKEND || '(not set)';
  const apiBase      = `${backendUrl}/api`;
  const erpLoginUrl  = process.env.REACT_APP_ERP_LOGIN_URL || 'https://cliqerp.retailcliq.cloud/login';
  const activationUrl= process.env.REACT_APP_ACTIVATION_BASE_URL || backendUrl;

  function copyValue(key, val) {
    navigator.clipboard.writeText(val).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1800);
    });
  }

  const rows = [
    { key: 'backend',    label: 'Backend Origin',       hint: 'REACT_APP_BACKEND_URL',             value: backendUrl,   testId: 'env-backend-url' },
    { key: 'api',        label: 'API Base Path',         hint: 'REACT_APP_BACKEND_URL + /api',      value: apiBase,      testId: 'env-api-base' },
    { key: 'erpLogin',   label: 'ERP Login URL',         hint: 'REACT_APP_ERP_LOGIN_URL',           value: erpLoginUrl,  testId: 'env-erp-login-url',    link: erpLoginUrl },
    { key: 'activation', label: 'Activation Base URL',   hint: 'REACT_APP_ACTIVATION_BASE_URL',     value: activationUrl,testId: 'env-activation-url',
      note: `Activation links: ${activationUrl}/api/signup/activate/<token>` },
  ];

  return (
    <div className="card" style={{ border: '1px solid #d1fae5', background: '#f0fdf4' }}
      data-testid="env-config-card">
      {/* Header / toggle */}
      <button
        onClick={() => setOpen(p => !p)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', background: 'transparent', border: 'none', cursor: 'pointer',
          borderBottom: open ? '1px solid #bbf7d0' : 'none'
        }}
        data-testid="env-config-toggle"
        aria-expanded={open}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Database size={15} color="#16a34a" strokeWidth={2.5} />
          <span style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>Environment Configuration</span>
          <span style={{ background: '#16a34a', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5, letterSpacing: 0.3 }}>Frontend .env</span>
          <span style={{ background: '#dcfce7', color: '#15803d', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 5 }}>Read-only · {rows.length} vars</span>
        </div>
        {open
          ? <ChevronDown size={14} color="#16a34a" />
          : <ChevronRight size={14} color="#16a34a" />}
      </button>

      {open && (
        <div className="card-body" style={{ padding: '16px 20px' }}>
          <p style={{ fontSize: 12, color: '#15803d', marginBottom: 14, lineHeight: 1.6 }}>
            All active <code>REACT_APP_*</code> environment variables baked into this build.
            To change any value, update <code style={{ margin: '0 4px' }}>frontend/.env</code> and restart the frontend via supervisor.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rows.map(row => (
              <div key={row.key}
                style={{ background: '#fff', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>
                    {row.label}
                    <span style={{ fontWeight: 400, textTransform: 'none', color: '#64748b', marginLeft: 6 }}>({row.hint})</span>
                  </div>
                  <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#0f172a', fontWeight: 600, wordBreak: 'break-all' }}
                    data-testid={row.testId}>
                    {row.value}
                  </div>
                  {row.link && (
                    <a href={row.link} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 11, color: '#0ea5e9', textDecoration: 'none', marginTop: 4, display: 'inline-block' }}>
                      Open ERP Login →
                    </a>
                  )}
                  {row.note && (
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, fontFamily: 'monospace' }}>{row.note}</div>
                  )}
                </div>
                <button
                  onClick={() => copyValue(row.key, row.value)}
                  title="Copy to clipboard"
                  style={{ flexShrink: 0, background: copiedKey === row.key ? '#dcfce7' : '#f8fafc', border: '1px solid #bbf7d0', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: copiedKey === row.key ? '#16a34a' : '#475569', transition: 'all 0.15s' }}
                  data-testid={`${row.testId}-copy`}
                >
                  {copiedKey === row.key
                    ? <><CheckCheck size={12} /> Copied!</>
                    : <><Copy size={12} /> Copy</>}
                </button>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 12, padding: '8px 12px', background: '#dcfce7', borderRadius: 6, fontSize: 10, color: '#15803d' }}>
            Backend API calls use <code>API</code> from <code>src/lib/api.js</code> — the single source of truth for <code>REACT_APP_BACKEND_URL</code>.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Key Settings ─────────────────────────────────────────────────────────────
// ── ClientCleanupPanel ────────────────────────────────────────────────────────
function ClientCleanupPanel() {
  const [search,    setSearch]    = useState('');
  const [clients,   setClients]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [selected,  setSelected]  = useState(null);  // selected client object
  const [dbStats,   setDbStats]   = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [preview,   setPreview]   = useState(null);
  const [otp,       setOtp]       = useState('');
  const [phase,     setPhase]     = useState('grid'); // grid|stats|preview|otp-sent|executing|done
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState('');

  // Load clients on mount
  useEffect(() => {
    setLoading(true);
    authFetch(`${API}/licensor/clients`).then(r => r.json())
      .then(d => setClients(d.clients || d || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    return !q || (c.ClientId||'').toLowerCase().includes(q)
      || (c.ClientName||c.clientName||'').toLowerCase().includes(q)
      || (c.tier_code||c.tier||'').toLowerCase().includes(q)
      || (c.industry_code||c.industry||'').toLowerCase().includes(q)
      || (c.SQLDBName||c.tenant_db_name||'').toLowerCase().includes(q);
  });

  const selectClient = async (client) => {
    setSelected(client); setPhase('stats'); setDbStats(null); setPreview(null); setError('');
    setStatsLoading(true);
    try {
      const r = await authFetch(`${API}/licensor/client-cleanup/db-stats`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ clientId: client.ClientId }) });
      const d = await r.json();
      setDbStats(d);
    } catch(e) { setDbStats({ error: e.message }); }
    setStatsLoading(false);
  };

  const doPreview = async () => {
    setPhase('loading'); setError(''); setPreview(null);
    try {
      const r = await authFetch(`${API}/licensor/client-cleanup/preview`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ clientId: selected.ClientId }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || d.error || 'Preview failed');
      setPreview(d); setPhase('preview');
    } catch(e) { setError(e.message); setPhase('stats'); }
  };

  const sendOtp = async () => {
    setPhase('loading');
    try {
      const r = await authFetch(`${API}/licensor/client-cleanup/send-otp`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ clientId: selected.ClientId }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'OTP send failed');
      setPhase('otp-sent');
    } catch(e) { setError(e.message); setPhase('preview'); }
  };

  const execute = async () => {
    if (otp.length < 6) { setError('Enter the 6-digit OTP'); return; }
    setPhase('executing'); setError('');
    try {
      const r = await authFetch(`${API}/licensor/client-cleanup/execute`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ clientId: selected.ClientId, otp }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || d.error || 'Execute failed');
      setResult(d); setPhase('done');
      setClients(prev => prev.filter(c => c.ClientId !== selected.ClientId));
    } catch(e) { setError(e.message); setPhase('otp-sent'); }
  };

  const reset = () => { setSelected(null); setPreview(null); setOtp(''); setPhase('grid'); setResult(null); setError(''); setDbStats(null); };

  const TIER_COLOR = { Lite:'#10b981', Express:'#06b6d4', Standard:'#3b82f6', Professional:'#8b5cf6', Enterprise:'#f59e0b' };
  const STATUS_COLOR = { ACTIVE:'#10b981', PENDING:'#f59e0b', SUSPENDED:'#ef4444', DEFAULT:'#64748b' };
  const s = (v) => STATUS_COLOR[v] || STATUS_COLOR.DEFAULT;
  const t = (v) => TIER_COLOR[v] || '#64748b';

  return (
    <div style={{ background:'#0f172a', border:'2px solid #ef4444', borderRadius:12, padding:20 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
        <AlertTriangle size={18} color="#ef4444" />
        <span style={{ color:'#ef4444', fontWeight:700, fontSize:15 }}>Client Cleanup — Danger Zone</span>
        <span style={{ background:'#7f1d1d', color:'#fca5a5', fontSize:11, padding:'2px 8px', borderRadius:20 }}>OTP-GATED · BACKUP-FIRST</span>
        {selected && phase !== 'done' && (
          <button onClick={reset} style={{ marginLeft:'auto', background:'transparent', color:'#64748b', border:'1px solid #334155', borderRadius:6, padding:'5px 12px', cursor:'pointer', fontSize:12 }}>
            ← Back to grid
          </button>
        )}
      </div>
      <p style={{ color:'#475569', fontSize:12, marginBottom:16 }}>Select a tenant to view DB statistics, then proceed with OTP-confirmed cleanup. Protected template DBs cannot be dropped.</p>

      {/* ── GRID phase ───────────────────────────────────────────────── */}
      {phase === 'grid' && (
        <>
          <input data-testid="cleanup-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by client ID, company, tier, industry, DB name…"
            style={{ width:'100%', background:'#1e293b', color:'#f1f5f9', border:'1px solid #334155', borderRadius:8, padding:'10px 14px', fontSize:13, marginBottom:16, boxSizing:'border-box' }} />
          {loading && <p style={{ color:'#64748b', fontSize:13 }}>Loading tenants…</p>}
          {!loading && filtered.length === 0 && <p style={{ color:'#64748b', fontSize:13 }}>No tenants found{search ? ' matching "'+search+'"' : ' — run a signup first'}.</p>}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:12 }}>
            {filtered.map(c => {
              const cid   = c.ClientId;
              const name  = c.ClientName || c.clientName || cid;
              const tier  = c.tier_code  || c.tier  || '—';
              const ind   = c.industry_code || c.industry || '—';
              const db    = c.SQLDBName  || c.tenant_db_name || '—';
              const status = c.Status || c.status || '—';
              return (
                <div key={cid} data-testid={`cleanup-client-card-${cid}`}
                  onClick={() => selectClient(c)}
                  style={{ background:'#1e293b', border:'1px solid #334155', borderRadius:10, padding:14, cursor:'pointer', transition:'all .15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor='#ef4444'}
                  onMouseLeave={e => e.currentTarget.style.borderColor='#334155'}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                    <span style={{ fontFamily:'monospace', color:'#60a5fa', fontSize:12, fontWeight:700 }}>{cid}</span>
                    <span style={{ fontSize:10, padding:'2px 7px', borderRadius:20, background: s(status)+'22', color: s(status), fontWeight:700 }}>{status}</span>
                  </div>
                  <p style={{ color:'#f1f5f9', fontWeight:600, fontSize:13, margin:'0 0 6px', lineHeight:1.3 }}>{name}</p>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                    <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4, background: t(tier)+'22', color: t(tier) }}>{tier}</span>
                    <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4, background:'#0f172a', color:'#64748b' }}>{ind}</span>
                  </div>
                  <p style={{ color:'#475569', fontFamily:'monospace', fontSize:10, margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{db}</p>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── STATS phase ─────────────────────────────────────────────── */}
      {(phase === 'stats' || phase === 'loading') && selected && (
        <div>
          {/* Client header */}
          <div style={{ background:'#1e293b', borderRadius:10, padding:16, marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <span style={{ fontFamily:'monospace', color:'#60a5fa', fontSize:13, fontWeight:700 }}>{selected.ClientId}</span>
              <span style={{ fontSize:11, padding:'3px 8px', borderRadius:20, background: s(selected.Status||'')+'22', color: s(selected.Status||'') }}>{selected.Status}</span>
            </div>
            <p style={{ color:'#f1f5f9', fontWeight:700, fontSize:15, margin:'0 0 6px' }}>{selected.ClientName||selected.clientName}</p>
            <p style={{ color:'#64748b', fontSize:12, margin:0, fontFamily:'monospace' }}>{selected.SQLDBName||selected.tenant_db_name||'No DB assigned'}</p>
          </div>

          {statsLoading && <p style={{ color:'#64748b', fontSize:13, padding:'20px 0' }}>Fetching DB statistics…</p>}

          {dbStats && !statsLoading && (
            <div>
              {/* Stats cards */}
              {!dbStats.error && (
                <>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:10, marginBottom:16 }}>
                    {[
                      { label:'DB Size', val: dbStats.dbSizeMB ? `${dbStats.dbSizeMB} MB` : '—', color:'#3b82f6' },
                      { label:'Tables w/ Data', val: dbStats.tables?.length ?? 0, color:'#10b981' },
                      { label:'Total Rows', val: (dbStats.tables||[]).reduce((s,t)=>s+(t.rows||0),0).toLocaleString(), color:'#8b5cf6' },
                      { label:'DB Created', val: dbStats.dbCreated ? 'Yes' : 'No', color: dbStats.dbCreated?'#10b981':'#ef4444' },
                    ].map(({ label, val, color }) => (
                      <div key={label} style={{ background:'#1e293b', border:`1px solid ${color}33`, borderRadius:8, padding:'12px 14px' }}>
                        <p style={{ color:'#64748b', fontSize:10, margin:'0 0 4px', textTransform:'uppercase', letterSpacing:'.08em' }}>{label}</p>
                        <p style={{ color, fontSize:18, fontWeight:800, margin:0, fontFamily:'monospace' }}>{String(val)}</p>
                      </div>
                    ))}
                  </div>

                  {/* Table row counts */}
                  {dbStats.tables?.length > 0 && (
                    <div style={{ background:'#1e293b', borderRadius:8, marginBottom:16, overflow:'hidden' }}>
                      <p style={{ color:'#94a3b8', fontSize:11, fontWeight:700, padding:'10px 14px 6px', textTransform:'uppercase', letterSpacing:'.1em', margin:0 }}>Table Data Summary</p>
                      <div style={{ maxHeight:200, overflowY:'auto' }}>
                        {dbStats.tables.map(row => (
                          <div key={row.table} style={{ display:'flex', justifyContent:'space-between', padding:'6px 14px', borderTop:'1px solid #0f172a', fontSize:12 }}>
                            <span style={{ color:'#cbd5e1', fontFamily:'monospace' }}>{row.table}</span>
                            <span style={{ color:'#60a5fa', fontFamily:'monospace' }}>{(row.rows||0).toLocaleString()} rows</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent logins */}
                  {dbStats.recentOps?.length > 0 && (
                    <div style={{ background:'#1e293b', borderRadius:8, marginBottom:16, overflow:'hidden' }}>
                      <p style={{ color:'#94a3b8', fontSize:11, fontWeight:700, padding:'10px 14px 6px', textTransform:'uppercase', letterSpacing:'.1em', margin:0 }}>Recent User Activity</p>
                      {dbStats.recentOps.map((u,i) => (
                        <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'6px 14px', borderTop:'1px solid #0f172a', fontSize:12 }}>
                          <span style={{ color:'#cbd5e1', fontFamily:'monospace' }}>{u.UserId} — {u.UserName}</span>
                          <span style={{ color:'#64748b' }}>{u.LastLogin ? new Date(u.LastLogin).toLocaleString() : 'Never'}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {dbStats.tables?.length === 0 && <p style={{ color:'#64748b', fontSize:12, marginBottom:16 }}>No data tables found in this DB — it may be empty or never seeded.</p>}
                </>
              )}
              {dbStats.error && <p style={{ color:'#ef4444', fontSize:12, marginBottom:12 }}>DB connect error: {dbStats.error}</p>}

              <div style={{ display:'flex', gap:10, marginTop:8 }}>
                <button data-testid="cleanup-preview-btn" onClick={doPreview}
                  style={{ background:'#7f1d1d', color:'#fca5a5', border:'1px solid #991b1b', borderRadius:6, padding:'9px 18px', fontWeight:700, cursor:'pointer', fontSize:13 }}>
                  Proceed to Cleanup ⟶
                </button>
                <button onClick={reset} style={{ background:'transparent', color:'#64748b', border:'1px solid #334155', borderRadius:6, padding:'9px 14px', cursor:'pointer', fontSize:13 }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PREVIEW phase ────────────────────────────────────────────── */}
      {(phase === 'preview' || phase === 'otp-sent' || phase === 'executing') && preview && (
        <div style={{ background:'#1e293b', borderRadius:8, padding:16 }}>
          <p style={{ color:'#f59e0b', fontWeight:700, fontSize:14, marginBottom:10 }}>⚠ Cleanup Confirmation — {preview.clientId}</p>
          {[
            ['Company', preview.clientName],
            ['Tier / Industry', `${preview.tier} / ${preview.industry}`],
            ['Physical DB', preview.physicalDB || 'None'],
            ['DB Size', preview.dbExists ? `${preview.dbSizeMB} MB ${preview.dbProtected ? '(PROTECTED — will NOT be dropped)' : '(backup will be taken first)'}` : 'DB not found'],
          ].map(([l,v]) => (
            <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #0f172a', fontSize:13 }}>
              <span style={{ color:'#64748b' }}>{l}</span><span style={{ color:'#f1f5f9', fontFamily:'monospace' }}>{v}</span>
            </div>
          ))}
          <p style={{ color:'#ef4444', fontWeight:600, fontSize:13, margin:'12px 0 6px' }}>Will delete:</p>
          {Object.entries(preview.willDelete||{}).filter(([,v])=>v).map(([k,v])=>(
            <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:12 }}>
              <span style={{ color:'#64748b' }}>{k}</span><span style={{ color:'#ef4444', fontFamily:'monospace' }}>{v}</span>
            </div>
          ))}
          {preview.dbExists && !preview.dbProtected && <p style={{ color:'#10b981', fontSize:12, marginTop:10 }}>Full SQL backup will be saved before drop.</p>}
          {preview.dbProtected && <p style={{ color:'#f59e0b', fontSize:12, marginTop:10 }}>This DB is a protected template — it will NOT be dropped, only records will be cleaned.</p>}

          {phase === 'preview' && (
            <div style={{ display:'flex', gap:10, marginTop:14 }}>
              <button data-testid="cleanup-send-otp-btn" onClick={sendOtp}
                style={{ background:'#ef4444', color:'#fff', border:'none', borderRadius:6, padding:'9px 18px', fontWeight:700, cursor:'pointer', fontSize:13 }}>
                Send OTP to msivan@myaccounts.in
              </button>
              <button onClick={reset} style={{ background:'#1e293b', color:'#94a3b8', border:'1px solid #334155', borderRadius:6, padding:'9px 14px', cursor:'pointer', fontSize:13 }}>Cancel</button>
            </div>
          )}

          {(phase === 'otp-sent' || phase === 'executing') && (
            <div style={{ marginTop:14 }}>
              <p style={{ color:'#10b981', fontSize:13, marginBottom:10 }}>OTP sent to <strong>msivan@myaccounts.in</strong> (valid 10 min).</p>
              <input data-testid="cleanup-otp-input" type="text" maxLength={6}
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                placeholder="_ _ _ _ _ _"
                style={{ background:'#0f172a', color:'#f59e0b', border:'2px solid #f59e0b', borderRadius:6, padding:'10px 14px', fontSize:22, fontWeight:800, letterSpacing:10, width:160, textAlign:'center' }} />
              <div style={{ display:'flex', gap:10, marginTop:12 }}>
                <button data-testid="cleanup-execute-btn" onClick={execute} disabled={otp.length<6||phase==='executing'}
                  style={{ background:otp.length===6?'#ef4444':'#7f1d1d', color:'#fff', border:'none', borderRadius:6, padding:'9px 18px', fontWeight:700, cursor:otp.length===6?'pointer':'not-allowed', fontSize:13 }}>
                  {phase==='executing'?'Executing…':'Execute Cleanup'}
                </button>
                <button onClick={reset} style={{ background:'#1e293b', color:'#94a3b8', border:'1px solid #334155', borderRadius:6, padding:'9px 14px', cursor:'pointer', fontSize:13 }}>Cancel</button>
                <button onClick={sendOtp} style={{ background:'transparent', color:'#64748b', border:'1px solid #334155', borderRadius:6, padding:'9px 14px', cursor:'pointer', fontSize:12 }}>Resend OTP</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── DONE phase ───────────────────────────────────────────────── */}
      {phase === 'done' && result && (
        <div style={{ background:'#052e16', border:'1px solid #16a34a', borderRadius:8, padding:16 }}>
          <p style={{ color:'#4ade80', fontWeight:700, fontSize:14, marginBottom:10 }}>✓ Cleanup Complete — {result.clientId}</p>
          {result.backupFile && <p style={{ color:'#86efac', fontSize:12 }}>Backup: <span style={{ fontFamily:'monospace', color:'#fde68a' }}>{result.backupFile}</span></p>}
          {result.isPoolDb && <p style={{ color:'#86efac', fontSize:12 }}>Pool DB {result.dbName} retained (other tenants share it).</p>}
          {(result.steps||[]).map((s,i) => (
            <div key={i} style={{ fontSize:12, color:s.ok?'#6ee7b7':'#fca5a5', fontFamily:'monospace' }}>{s.ok?'✓':'✗'} {s.step}: {s.detail}</div>
          ))}
          <button onClick={reset} style={{ marginTop:12, background:'#1e293b', color:'#94a3b8', border:'1px solid #334155', borderRadius:6, padding:'7px 14px', cursor:'pointer', fontSize:12 }}>
            Back to Client Grid
          </button>
        </div>
      )}

      {error && <p style={{ color:'#ef4444', fontSize:13, marginTop:10 }}>⚠ {error}</p>}
    </div>
  );
}


export default function KeySettings() {


  const { fingerprint: currentFP } = useSecurity();
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [result, setResult]       = useState(null);
  const [copied, setCopied]       = useState(false);
  const [form, setForm]           = useState({
    primary: '', coOwner: '', child1: '', child2: '',
    family: '', dob1: '', anniversary: ''
  });

  // Load current config
  useEffect(() => {
    authFetch(`${API}/security/dna-config`)
      .then(r => r.json())
      .then(d => {
        setForm({ primary: d.primary||'', coOwner: d.coOwner||'', child1: d.child1||'',
          child2: d.child2||'', family: d.family||'', dob1: d.dob1||'', anniversary: d.anniversary||'' });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const regenerate = async () => {
    if (!form.primary || !form.coOwner || !form.family) {
      alert('Primary Owner, Co-Owner, and Family Name are required.');
      return;
    }
    setSaving(true);
    setResult(null);
    try {
      const res  = await authFetch(`${API}/security/dna-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      setResult(data);
    } catch (e) { alert('Failed to update DNA config.'); }
    finally { setSaving(false); }
  };

  const copySnippet = (text) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); });
  };

  const [activeTab, setActiveTab] = useState('dna');

  const TABS = [
    { id: 'dna',       label: 'DNA Config',    icon: <Fingerprint size={14} /> },
    { id: 'downloads', label: 'Downloads',     icon: <Download size={14} /> },
    { id: 'provision', label: 'Provisioning',  icon: <Zap size={14} /> },
    { id: 'env',       label: 'Environment',   icon: <Terminal size={14} /> },
    { id: 'sql',       label: 'SQL Access',    icon: <Database size={14} /> },
    { id: 'cleanup',   label: 'Cleanup',       icon: <AlertTriangle size={14} />, danger: true },
  ];

  if (loading) {
    return <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>;
  }

  const tabBar = (
    <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #1e293b', marginBottom: 24, overflowX: 'auto', paddingBottom: 0, flexShrink: 0 }}>
      {TABS.map(t => {
        const active = activeTab === t.id;
        return (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            data-testid={`ks-tab-${t.id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
              background: active ? (t.danger ? '#7f1d1d' : '#0f172a') : 'transparent',
              color: active ? (t.danger ? '#fca5a5' : '#60a5fa') : (t.danger ? '#ef4444' : '#64748b'),
              border: 'none', borderBottom: active ? `2px solid ${t.danger ? '#ef4444' : '#60a5fa'}` : '2px solid transparent',
              marginBottom: -2, cursor: 'pointer', fontWeight: active ? 700 : 500,
              fontSize: 13, borderRadius: '6px 6px 0 0', whiteSpace: 'nowrap', transition: 'all .15s',
            }}>
            {t.icon}{t.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 900 }}>
      {/* Page title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ width: 38, height: 38, background: '#0f172a', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Settings size={18} color="#60a5fa" />
        </div>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', margin: 0 }}>Key Settings</h2>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>DNA · Downloads · Provisioning · Environment · SQL · Cleanup</p>
        </div>
      </div>

      {tabBar}

      {/* ── TAB: DNA Config ─────────────────────────────────────────── */}
      {activeTab === 'dna' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Current fingerprint */}
          <div className="dna-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <Fingerprint size={20} color="#60a5fa" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Current Active Fingerprint</span>
            </div>
            <div className="hash-display">{currentFP || 'Loading...'}</div>
            <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>SHA-256 of SHA-512(15-round) MASTER_SECRET — safe to share publicly</div>
          </div>

          {/* Edit form */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Identity Configuration</span>
              <span className="card-badge">AES-256-GCM BOUND</span>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label className="form-label">Primary Owner Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="form-input" data-testid="ks-primary" value={form.primary} onChange={e => f('primary', e.target.value)} placeholder="e.g. Manchala Nageshwar Rao" />
                </div>
                <div>
                  <label className="form-label">Co-Owner Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="form-input" data-testid="ks-coowner" value={form.coOwner} onChange={e => f('coOwner', e.target.value)} placeholder="e.g. Manchala Sunitha" />
                </div>
                <div>
                  <label className="form-label">Child 1</label>
                  <input className="form-input" data-testid="ks-child1" value={form.child1} onChange={e => f('child1', e.target.value)} placeholder="e.g. Manchala Akhilesh" />
                </div>
                <div>
                  <label className="form-label">Child 2</label>
                  <input className="form-input" data-testid="ks-child2" value={form.child2} onChange={e => f('child2', e.target.value)} placeholder="e.g. Manchala Divya" />
                </div>
                <div>
                  <label className="form-label">Family Name / Clan <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="form-input" data-testid="ks-family" value={form.family} onChange={e => f('family', e.target.value)} placeholder="e.g. Manchala" />
                </div>
                <div>
                  <label className="form-label">Primary DOB (DDMMYYYY)</label>
                  <input className="form-input" data-testid="ks-dob1" value={form.dob1} onChange={e => f('dob1', e.target.value)} placeholder="e.g. 01011980" maxLength={8} />
                </div>
                <div>
                  <label className="form-label">Wedding Anniversary (DDMMYYYY)</label>
                  <input className="form-input" data-testid="ks-anniversary" value={form.anniversary} onChange={e => f('anniversary', e.target.value)} placeholder="e.g. 14021998" maxLength={8} />
                </div>
              </div>
              <button className="btn btn-primary" onClick={regenerate} disabled={saving} data-testid="ks-regenerate-btn"
                style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8 }}>
                {saving ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Regenerating…</> : <><RefreshCw size={14} /> Regenerate Signature</>}
              </button>
            </div>
          </div>

          {/* Result */}
          {result && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {result.ok ? (
                <>
                  <div className="alert-banner green">
                    <CheckCircle2 size={14} color="#16a34a" style={{ flexShrink: 0 }} />
                    <div style={{ fontSize: 12 }}>
                      <strong>Signature regenerated.</strong> New fingerprint: <code style={{ background: '#dcfce7', padding: '1px 6px', borderRadius: 3 }}>{result.newFingerprint?.slice(0,16)}…</code>
                    </div>
                  </div>
                  <div className="card">
                    <div className="card-header">
                      <span className="card-title">New DNA Fingerprint (SHA-256)</span>
                      <span className="card-badge">SAFE TO SHARE</span>
                    </div>
                    <div className="card-body">
                      <div className="hash-display" style={{ marginBottom: 8 }}>{result.newFingerprint}</div>
                    </div>
                  </div>
                  <div className="card">
                    <div className="card-header">
                      <span className="card-title">Encrypted .env Snippet</span>
                      <button onClick={() => copySnippet(result.encEnvSnippet)} className="btn btn-outline" style={{ fontSize: 12, padding: '4px 12px' }} data-testid="ks-copy-btn">
                        {copied ? <><CheckCheck size={11} /> Copied!</> : <><Copy size={11} /> Copy</>}
                      </button>
                    </div>
                    <pre style={{ background: '#0f172a', color: '#60a5fa', fontFamily: 'monospace', fontSize: 12, padding: '20px 24px', margin: 0, borderRadius: '0 0 12px 12px', overflowX: 'auto', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{result.encEnvSnippet}</pre>
                  </div>
                </>
              ) : (
                <div className="alert-banner red">
                  <AlertTriangle size={14} color="#dc2626" style={{ flexShrink: 0 }} />
                  <div style={{ fontSize: 12, color: '#991b1b' }}>Error: {result.error || 'Failed to regenerate signature'}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: Downloads ──────────────────────────────────────────── */}
      {activeTab === 'downloads' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <ZipDownloadPanel
            title="Complete Integration Package"
            filename="msn-cliq-security-integration.zip"
            description="All backend security modules, React integration file, encrypted .env template, and step-by-step README — drop into any ERP project."
            downloadUrl="/security/download-integration"
            color="#3b82f6" accentBg="#eff6ff"
            testId="ks-integration-panel"
            badges={['env-cipher.js','identity-dna.js','crypto-engine.js','blockchain.js','signature-guard.js','webhook-hmac.js','auth.js','db.js','SecurityIntegration.jsx','SecurityContext.js','SecurityStyles.css','useDevToolsGuard.js','.env.template','INTEGRATION_README.md','docs/SIGNUP_PROJECT_INTEGRATION.md','docs/ERP_PROJECT_INTEGRATION.md']}
          />
          <ZipDownloadPanel
            title="RetailCLIQ Webpage Pack"
            filename="retailcliq-webpage-pack.zip"
            description="Public-facing signup page (tier picker + mock payment) plus live industry × tier catalogue. No secrets — safe to host on Vercel / Netlify / S3."
            downloadUrl="/security/download-webpage-pack"
            previewUrl="/security/signup-preview"
            color="#10b981" accentBg="#ecfdf5"
            testId="ks-webpage-panel"
            badges={['signup/signup.html','signup/signup.js','signup/mock-razorpay.html','industry/industries.json','industry/pricing-tiers.json','industry/INDEX.json','WEBPAGE_README.md','docs/SIGNUP_PROJECT_INTEGRATION.md']}
          />
          <div className="card">
            <div className="card-header"><span className="card-title">Quick Integration Steps</span></div>
            <div className="card-body">
              <ol style={{ paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
                <li>Unzip → copy <code style={{ background:'#f1f5f9', padding:'1px 5px', borderRadius:3 }}>backend/security-node/</code> into your project's backend.</li>
                <li>Paste the <strong>encrypted .env snippet</strong> (DNA Config tab) into your project's <code>.env</code>.</li>
                <li>Copy <code>frontend/src/security/SecurityIntegration.jsx</code> into your React project.</li>
                <li>Set <code>VITE_SECURITY_API=https://this-url/api</code> in your Vite <code>.env</code>.</li>
                <li>Wrap root: <code>{'<SecurityGate><App /></SecurityGate>'}</code></li>
                <li>Add topbar: <code>{'<SecurityStatusBar />'}</code></li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB: Provisioning ───────────────────────────────────────── */}
      {activeTab === 'provision' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <WebhookTestPanel />
          <BulkResyncPanel />
        </div>
      )}

      {/* ── TAB: Environment ────────────────────────────────────────── */}
      {activeTab === 'env' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <EnvironmentConfigCard />
          <ProductionERPIntegrationPanel />
        </div>
      )}

      {/* ── TAB: SQL Access ─────────────────────────────────────────── */}
      {activeTab === 'sql' && (
        <SqlCredentialsManager />
      )}

      {/* ── TAB: Cleanup ────────────────────────────────────────────── */}
      {activeTab === 'cleanup' && (
        <ClientCleanupPanel />
      )}
    </div>
  );
}

